import React from "react";
import { useParams } from "react-router-dom";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import { GetIntellexusDate } from "../../components/forms/common/intellexusDate/IntellexusDateField";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewLinksField from "../../components/view/fields/ViewLinksField";
import ViewRangeField from "../../components/view/fields/ViewRangeField";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewTitlesAccordion from "../../components/view/fields/ViewTitlesAccordion";
import { useEntityHandlerContext } from "../../controllers/entityController";
import useVersionController from "../../controllers/versionController";
import useVersionLogic from "../../hooks/logic/versionLogic";
import {
  formatDoxographicalCategory,
  formatStringArray,
  formatSystemLanguage,
} from "../../common/formatters/workFormatters";
import { Version, VersionCatalog } from "../../types/version";
import { VersionTeamConnection } from "../../types/versionTeam";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import VersionTextsViewSection from "./sections/view/VersionTextsViewSection";
import ContributorsViewSection from "./sections/view/ContributorsViewSection";
import VersionTeamsViewSection from "./sections/view/VersionTeamsViewSection";
import ReferenceVersionsViewSection from "./sections/view/ReferenceVersionsViewSection";

const formatCatalog = (value: VersionCatalog | null | undefined): string => {
  if (!value || (!value.catalog_no && !value.catalog_entry)) return "N/A";
  if (value.catalog_no && value.catalog_entry) {
    return `${value.catalog_no}: ${value.catalog_entry}`;
  }
  return value.catalog_no || value.catalog_entry || "N/A";
};

interface ViewVersionPageProps {
  systemLanguageType: Version["system_language_type"];
}

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      {value}
    </Box>
  </Grid>
);

const VersionReferencesContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Version>();
  const version = methods.getValues();

  return (
    <DetailField
      label="Work"
      value={
        version.work ? (
          <EntityReferenceComponent entity={version.work} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
    />
  );
};

const VersionDateContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Version>();
  const version = methods.getValues();

  return (
    <DetailField
      label="Version Date"
      value={
        <Typography variant="body1">
          {version.version_date ? GetIntellexusDate(version.version_date) : "N/A"}
        </Typography>
      }
    />
  );
};

const ViewVersionPage: React.FC<ViewVersionPageProps> = ({
  systemLanguageType,
}) => {
  const { _id } = useParams<{ _id: string }>();

  if (!_id) {
    throw new Error("Version ID is required for viewing.");
  }

  const controller = useVersionController(_id, systemLanguageType);
  const { showTibetanFields, showField } = useVersionLogic(systemLanguageType);

  return (
    <ViewPage<Version>
      controller={controller}
      sections={(version) => [
        <VersionTextsViewSection key="texts" version={version} />,
        <ContributorsViewSection
          key="contributors"
          anchor={version}
          systemLanguageType={systemLanguageType}
        />,
        ...(systemLanguageType === SystemLanguages.tibetan ||
        systemLanguageType === SystemLanguages.chinese
          ? [
              <VersionTeamsViewSection
                key="versionTeams"
                anchor={version}
                systemLanguageType={
                  systemLanguageType as VersionTeamConnection["system_language_type"]
                }
              />,
            ]
          : []),
        <ReferenceVersionsViewSection key="referenceVersions" anchor={version} />,
      ]}
    >
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
        <ViewTextField name="name" label="Name" />
        <ViewTextField
          name="system_language_type"
          label="System Language"
          textParser={formatSystemLanguage}
        />
      </ViewFieldCard>

      <ViewFieldCard title="Linked References">
        <VersionReferencesContent />
      </ViewFieldCard>

      <ViewFieldCard title="Version Details">
        <ViewTextField name="completeness" label="Completeness" />
        <VersionDateContent />
      </ViewFieldCard>

      <ViewFieldCard title="Composition">
        {showField("date_of_composition") ? (
          <ViewRangeField
            name="date_of_composition"
            label="Date of Composition"
          />
        ) : null}
        {showField("link_to_other_databases") ? (
          <ViewLinksField
            name="link_to_other_databases"
            label="Links to Other Databases"
          />
        ) : null}
      </ViewFieldCard>

      {showField("title") ? (
        <ViewFieldCard title="Titles and Evidence">
          <ViewTitlesAccordion />
        </ViewFieldCard>
      ) : null}

      <ViewFieldCard title="Classification">
        {showField("doxographical_category") ? (
          <ViewTextField
            name="doxographical_category"
            label="Doxographical Category"
            textParser={formatDoxographicalCategory}
          />
        ) : null}
        {showField("transfered_collection") ? (
          <ViewTextField
            name="transfered_collection"
            label="Transfered Collection"
          />
        ) : null}
        {showField("catalog") ? (
          <ViewTextField
            name="catalog"
            label="Catalog"
            textParser={formatCatalog}
          />
        ) : null}
        {showField("language") ? (
          <ViewTextField
            name="language"
            label="Language"
            textParser={formatStringArray}
          />
        ) : null}
        {showField("status_of_indic_origin") ? (
          <ViewTextField
            name="status_of_indic_origin"
            label="Status of Indic Origin"
            textParser={formatStringArray}
          />
        ) : null}
      </ViewFieldCard>

      {showTibetanFields ? (
        <ViewFieldCard title="Tibetan Details">
          <ViewTextField name="collection" label="Collection" />
          <ViewTextField name="source_language" label="Source Language" />
          <ViewTextField name="vol_number" label="Volume Number" />
          <ViewTextField name="vol_name" label="Volume Name" />
          <ViewTextField name="folio_start" label="Folio/Page Start" />
          <ViewTextField name="folio_end" label="Folio/Page End" />
        </ViewFieldCard>
      ) : null}

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

export default ViewVersionPage;
